import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/helpers/axios";
import { useAuth } from "@/hooks";
import { FormsApplicationsQueryData } from "../_types/formsApplications.types";

export const useMarkFormApplicationAsRead = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.patch(`/v2/inbox/forms/applications/${id}/read-status`, {
        read: true,
      });
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ["forms-applications"] });

      const previousQueries =
        queryClient.getQueriesData<FormsApplicationsQueryData>({
          queryKey: ["forms-applications"],
        });

      queryClient.setQueriesData<FormsApplicationsQueryData>(
        { queryKey: ["forms-applications"] },
        (currentData) => {
          if (!currentData) return currentData;

          const readAt = Date.now();
          const readByName =
            user && [user.firstName, user.lastName].filter(Boolean).join(" ").trim();

          return {
            ...currentData,
            items: currentData.items.map((application) =>
              application.id === id
                ? {
                    ...application,
                    read: true,
                    readByUserId: user?.id ?? null,
                    readByName: readByName || user?.email || null,
                    readAt,
                  }
                : application
            ),
          };
        }
      );

      return { previousQueries };
    },
    onError: (_error, _id, context) => {
      context?.previousQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["forms-applications"] });
    },
  });
};
